import type { CSSProperties } from 'react';
import { pickReadableText } from '@platzona/platform-theme';
import { isSaasInternalMediaSrc, toStoredMediaUrl } from './mediaEmbed';
import { isCssBackgroundValue, isImageBackgroundValue } from './slideBackground';

/** Optional per-block design overrides (all ~520 templates via shared editor + SiteRenderer). */
export interface BlockDesign {
  /**
   * Section background: CSS solid/gradient **or** a SaaS media library path
   * (`/api/saas/v1/media/:id/file`). Images cover the block (center / no-repeat).
   */
  background?: string;
  textColor?: string;
  headingColor?: string;
  /** Local CSS font stack only — no Google Fonts / CDN. */
  fontFamily?: string;
  fontSize?: 'sm' | 'md' | 'lg' | 'xl' | 'display';
  fontWeight?: 'normal' | 'semibold' | 'bold';
}

/** Local stacks already used by theme / app chrome (no external font URLs). */
export const BLOCK_FONT_STACKS = [
  {
    id: 'sans',
    value: 'Source Sans 3, Segoe UI, system-ui, sans-serif',
    label: 'Source Sans',
  },
  {
    id: 'serif',
    value: 'Source Serif 4, Georgia, serif',
    label: 'Source Serif',
  },
  {
    id: 'system',
    value: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    label: 'System UI',
  },
  {
    id: 'mono',
    value: 'SF Mono, Fira Code, Menlo, Consolas, monospace',
    label: 'Monospace',
  },
] as const;

const FONT_SIZE_MAP = {
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.35rem',
  display: 'clamp(1.75rem, 3.5vw, 2.35rem)',
} as const;

/** Hero/carousel caption sizes for Design → Font size (larger than body presets). */
export const CAROUSEL_TITLE_SIZE_MAP = {
  sm: '1.35rem',
  md: '1.75rem',
  lg: '2.25rem',
  xl: '2.75rem',
  display: 'clamp(2.5rem, 5vw, 3.5rem)',
} as const;

export const CAROUSEL_SUBTITLE_SIZE_MAP = {
  sm: '0.9rem',
  md: '1rem',
  lg: '1.1rem',
  xl: '1.2rem',
  display: '1.35rem',
} as const;

export const CAROUSEL_BUTTON_SIZE_MAP = {
  sm: '0.85rem',
  md: '0.9rem',
  lg: '0.95rem',
  xl: '1rem',
  display: '1.05rem',
} as const;

const FONT_WEIGHT_MAP = { normal: '400', semibold: '600', bold: '700' } as const;
const ALLOWED_SIZES = new Set(['sm', 'md', 'lg', 'xl', 'display']);
const ALLOWED_WEIGHTS = new Set(['normal', 'semibold', 'bold']);
const ALLOWED_FONTS = new Set<string>(BLOCK_FONT_STACKS.map((f) => f.value));

/**
 * Allow safe CSS color / gradient values for block backgrounds and text colors.
 * Strips javascript:, expression(), and url() (external / data images).
 */
export function sanitizeCssPaintValue(raw: unknown, maxLen = 2000): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const v = raw.trim().slice(0, maxLen);
  if (!v) return undefined;
  if (/javascript\s*:/i.test(v)) return undefined;
  if (/expression\s*\(/i.test(v)) return undefined;
  if (/url\s*\(/i.test(v)) return undefined;
  if (/@import/i.test(v)) return undefined;
  if (isCssBackgroundValue(v)) return v;
  // Simple CSS named colors (local, no functions)
  if (/^[a-z]{3,20}$/i.test(v)) return v.toLowerCase();
  return undefined;
}

/**
 * Section background: CSS paint **or** local SaaS media library URL.
 * External CDN / `url(...)` CSS images are rejected (local-only).
 */
export function sanitizeBlockBackgroundValue(raw: unknown, maxLen = 2000): string | undefined {
  const paint = sanitizeCssPaintValue(raw, maxLen);
  if (paint) return paint;
  if (typeof raw !== 'string') return undefined;
  const v = raw.trim().slice(0, maxLen);
  if (!v) return undefined;
  if (/javascript\s*:/i.test(v)) return undefined;
  if (!isImageBackgroundValue(v) && !isSaasInternalMediaSrc(v)) return undefined;
  if (!isSaasInternalMediaSrc(v)) return undefined;
  const stored = toStoredMediaUrl(v);
  return stored || undefined;
}

/** True when `design.background` is a media image (not CSS paint). */
export function isBlockDesignImageBackground(background?: string | null): boolean {
  if (!background) return false;
  return isSaasInternalMediaSrc(background) || isImageBackgroundValue(background);
}

function cssUrlValue(path: string): string {
  return `url("${path.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`;
}

/** Normalize a raw `props.design` value into a clean BlockDesign (or undefined). */
export function parseBlockDesign(raw: unknown): BlockDesign | undefined {
  if (raw == null) return undefined;
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    try {
      obj = JSON.parse(trimmed);
    } catch {
      return undefined;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return undefined;
  const d = obj as Record<string, unknown>;
  const out: BlockDesign = {};

  const background = sanitizeBlockBackgroundValue(d.background);
  if (background) out.background = background;

  const textColor = sanitizeCssPaintValue(d.textColor, 2000);
  if (textColor) out.textColor = textColor;

  const headingColor = sanitizeCssPaintValue(d.headingColor, 2000);
  if (headingColor) out.headingColor = headingColor;

  if (typeof d.fontFamily === 'string' && ALLOWED_FONTS.has(d.fontFamily.trim())) {
    out.fontFamily = d.fontFamily.trim();
  }

  if (typeof d.fontSize === 'string' && ALLOWED_SIZES.has(d.fontSize)) {
    out.fontSize = d.fontSize as BlockDesign['fontSize'];
  }

  if (typeof d.fontWeight === 'string' && ALLOWED_WEIGHTS.has(d.fontWeight)) {
    out.fontWeight = d.fontWeight as BlockDesign['fontWeight'];
  }

  return Object.keys(out).length ? out : undefined;
}

/** Persist-ready design object (drops empty / unsafe fields). */
export function sanitizeBlockDesign(raw: unknown): BlockDesign | undefined {
  return parseBlockDesign(raw);
}

export function isBlockDesignEmpty(design?: BlockDesign | null): boolean {
  return !design || Object.keys(design).length === 0;
}

/**
 * Sample a solid hex (or first/last stop of a simple linear-gradient) for luminance.
 * Returns undefined when the paint cannot be sampled safely.
 */
export function sampleBackgroundLuminanceHex(background: string): string | undefined {
  const v = background.trim();
  if (!v) return undefined;
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) {
    const raw = v.slice(1);
    const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
    return `#${full.toLowerCase()}`;
  }
  const linear = v.match(/^linear-gradient\(\s*[\d.]+deg\s*,(.+)\)$/i);
  if (!linear) return undefined;
  const body = linear[1];
  const hexes = [...body.matchAll(/#([0-9a-f]{3}|[0-9a-f]{6})\b/gi)].map((m) => {
    const raw = m[1];
    const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
    return `#${full.toLowerCase()}`;
  });
  if (hexes.length === 0) return undefined;
  // Prefer the darker stop so light text is chosen on dark gradients.
  let darkest = hexes[0];
  let darkestLum = Infinity;
  for (const hex of hexes) {
    const rgb = hex.slice(1);
    const r = Number.parseInt(rgb.slice(0, 2), 16) / 255;
    const g = Number.parseInt(rgb.slice(2, 4), 16) / 255;
    const b = Number.parseInt(rgb.slice(4, 6), 16) / 255;
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    if (lum < darkestLum) {
      darkestLum = lum;
      darkest = hex;
    }
  }
  return darkest;
}

/**
 * Auto text/heading colors when the user set a background but left text colors unset.
 * Never overrides explicit `textColor` / `headingColor`.
 */
export function resolveAutoBlockTextColors(design: BlockDesign): {
  textColor?: string;
  headingColor?: string;
} {
  if (!design.background) return {};
  if (design.textColor && design.headingColor) return {};
  const sample = sampleBackgroundLuminanceHex(design.background);
  if (!sample) return {};
  const auto = pickReadableText(sample);
  return {
    textColor: design.textColor || auto,
    headingColor: design.headingColor || auto,
  };
}

/**
 * CSS custom properties + direct paint for `.site-block` roots.
 * Children inherit color / font; headings pick up `--block-heading-color`.
 */
export function blockDesignToStyle(design?: BlockDesign | null): CSSProperties | undefined {
  if (isBlockDesignEmpty(design) || !design) return undefined;
  const style: CSSProperties & Record<string, string> = {};
  const autoText = resolveAutoBlockTextColors(design);

  if (design.background) {
    if (isBlockDesignImageBackground(design.background)) {
      const url = cssUrlValue(design.background);
      style.backgroundImage = url;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
      style.backgroundRepeat = 'no-repeat';
      style['--block-bg'] = 'transparent';
      style['--block-bg-image'] = url;
    } else {
      style.background = design.background;
      style['--block-bg'] = design.background;
    }
  }
  const textColor = design.textColor || autoText.textColor;
  if (textColor) {
    style['--block-text'] = textColor;
    if (/gradient\s*\(/i.test(textColor)) {
      style['--block-text-is-gradient'] = '1';
    } else {
      style.color = textColor;
    }
  }
  const headingColor = design.headingColor || autoText.headingColor;
  if (headingColor) {
    style['--block-heading-color'] = headingColor;
    if (/gradient\s*\(/i.test(headingColor)) {
      style['--block-heading-is-gradient'] = '1';
    }
  }
  if (design.fontFamily) {
    style.fontFamily = design.fontFamily;
    style['--block-font'] = design.fontFamily;
  }
  if (design.fontSize) {
    const size = FONT_SIZE_MAP[design.fontSize];
    style.fontSize = size;
    style['--block-font-size'] = size;
    const titleSize = CAROUSEL_TITLE_SIZE_MAP[design.fontSize];
    const subSize = CAROUSEL_SUBTITLE_SIZE_MAP[design.fontSize];
    const btnSize = CAROUSEL_BUTTON_SIZE_MAP[design.fontSize];
    if (titleSize) style['--carousel-title-size'] = titleSize;
    if (subSize) style['--carousel-subtitle-size'] = subSize;
    if (btnSize) style['--carousel-button-size'] = btnSize;
  }
  if (design.fontWeight) {
    const weight = FONT_WEIGHT_MAP[design.fontWeight];
    style.fontWeight = weight as CSSProperties['fontWeight'];
    style['--block-font-weight'] = weight;
  }

  return style;
}

/** Patch a design field; clears key when value is empty. Returns undefined when empty. */
export function patchBlockDesign(
  current: BlockDesign | undefined,
  patch: Partial<BlockDesign>
): BlockDesign | undefined {
  const next: BlockDesign = { ...(current || {}) };
  for (const [key, value] of Object.entries(patch) as Array<[keyof BlockDesign, string | undefined]>) {
    if (value == null || value === '') {
      delete next[key];
    } else {
      (next as Record<string, string>)[key] = value;
    }
  }
  return sanitizeBlockDesign(next);
}
